import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, CalendarDays, Users } from "lucide-react";
import { Button, PillarCard, Section } from "@alight/ui";
import { useSeo } from "../lib/seo";
import { useBlock } from "../lib/content";
import { seasonStartLabel } from "../lib/season";
import { CmsText } from "../components/CmsText";
import { PageIntro } from "../components/PageIntro";

export const Route = createFileRoute("/what-happens")({
  component: WhatHappensPage,
});

function WhatHappensPage() {
  const c = useBlock("whatHappens");
  const season = useBlock("season");
  const banners = useBlock("banners");

  useSeo({
    title: "What happens at Connect? | Connect Sydney",
    description:
      "After the HSC, Connect meets on Mondays across Sydney for Bible talks, workshops, games and food — then Campus Time with your uni Christian group for the final three weeks.",
    path: "/what-happens",
    image: banners.whatHappensImage,
  });
  const startLabel = seasonStartLabel(season.sessions);

  return (
    <>
      <PageIntro
        eyebrow="The program"
        title="What happens at Connect?"
        description={c.intro}
        image={banners.whatHappensImage}
        imageAlt={banners.whatHappensImageAlt}
        actions={
          <Button asChild variant="accent">
            <Link to="/sessions">
              <CalendarDays className="mr-1.5 h-4 w-4" aria-hidden />
              Sessions this season
            </Link>
          </Button>
        }
      />

      <Section tone="paper" spacing="lg">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <img
            src={c.image1}
            alt={c.image1Alt}
            width={1200}
            height={800}
            loading="eager"
            className="w-full rounded-card object-cover shadow-card"
          />
          <div>
            <h2 className="font-heading text-3xl font-extrabold text-ink">Bible talks & workshops</h2>
            <p className="mt-4 text-lg text-muted">{c.topicsIntro}</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {c.topics.map((t, i) => (
                <li key={i} className="flex items-center gap-3 rounded-card border border-border bg-card px-4 py-3">
                  <BookOpen className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                  <span className="font-semibold text-ink">{t.title}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-muted">
              Mondays {season.time}, {startLabel.replace(/^Starts\s+/i, "from ")}.{" "}
              {season.dinnerNote}
            </p>
          </div>
        </div>
      </Section>

      <Section tone="card" spacing="lg">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="lg:order-2">
            <img
              src={c.image2}
              alt={c.image2Alt}
              width={465}
              height={310}
              loading="lazy"
              className="w-full rounded-card object-cover shadow-card"
            />
          </div>
          <div className="lg:order-1">
            <h2 className="font-heading text-3xl font-extrabold text-ink">{c.campusTimeTitle}</h2>
            <div className="mt-4 space-y-4 text-lg leading-relaxed text-muted">
              <CmsText text={c.campusTimeBody} />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/campus-time">More about Campus Time</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/find-us">Find a location</Link>
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <Section spacing="lg">
        <div className="mx-auto max-w-3xl">
          <PillarCard
            as="h2"
            icon={<Users className="h-6 w-6" />}
            title="Not heading to uni?"
            description={c.notUniNote}
          />
        </div>
      </Section>
    </>
  );
}
